import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { CriticalAlertsComponent } from '../critical-alerts/critical-alerts.component';
import { MaintenanceStatus } from '../../../../_shared/enums/maintenance-status.enum';
import { MaintenanceModel } from '../../../../_shared/enums/maintenance-model.enum';
import { MaintenanceDetailsResponse } from '../../models/maintenance-overview-response.model';
import { EditMaintenanceModalComponent } from '../modals/edit-maintenance-modal/edit-maintenance-modal.component';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { createDeleteMaintenanceConfirmation } from '../../constants/maintenance-confirmation.constants';
import { MaintenanceSource } from '../../../../_shared/enums/maintenance-source.enum';
import { ServiceTypes } from '../../../../_shared/enums/service-type.enum';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { AuthService } from '../../../../../_shared/services';
import { MaintenanceOverviewService } from '../../services/maintenance-overview.service';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DeleteMaintenanceRequest } from '../../models/maintenance-overview-request.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-maintenance-overview-all-table',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    SearchComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    PaginationComponent,
    CriticalAlertsComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './maintenance-overview-all-table.component.html',
  styleUrl: './maintenance-overview-all-table.component.scss'
})
export class MaintenanceOverviewAllTableComponent {
  @Input() maintenanceData: MaintenanceDetailsResponse[] = [];
  @Input() isLoading: boolean = false;
  @Input() hasError: boolean = false;
  @Input() searchTerm: string = '';
  @Input() accountIds: number[] = [];
  @Input() currentPage: number = 1;
  @Input() pageSize: number = 10;
  @Input() totalCount: number = 0;
  @Input() totalPages: number = 1;

  @Output() searchTermUpdated = new EventEmitter<string>();
  @Output() exportClicked = new EventEmitter<void>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() dataRefreshNeeded = new EventEmitter<void>();

  currencySymbol: string = '';
  currencyCode: string = '';
  maintenanceStatus = MaintenanceStatus;
  private transloco = inject(TranslocoService);

  serviceTypeLabels: Record<ServiceTypes, string> = {
    [ServiceTypes.Minor]: this.transloco.translate('maintenanceOverview.serviceTypeShort.minor'),
    [ServiceTypes.Intermediate]: this.transloco.translate('maintenanceOverview.serviceTypeShort.intermediate'),
    [ServiceTypes.Major]: this.transloco.translate('maintenanceOverview.serviceTypeShort.major')
  };

  private cancelSubject: Subject<void> = new Subject<void>();

  private ngbModal = inject(NgbModal);
  private authService = inject(AuthService);
  private confirmationModalService = inject(ConfirmationModalService);
  private maintenanceService = inject(MaintenanceOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);

  constructor() {
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  get showingCount(): number {
    return Math.min((this.currentPage - 1) * this.pageSize + this.pageSize, this.totalCount);
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTermUpdated.emit(searchTerm);
  }

  onExportClick(): void {
    this.exportClicked.emit();
  }

  onPageChange(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
      this.pageChanged.emit(page);
    }
  }

  async onEditClick(itemId: number): Promise<void> {
    const modalRef = this.ngbModal.open(EditMaintenanceModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });

    try {
      this.cancelSubject.next();
      const response = await this.maintenanceService.getAssetMaintenanceById(itemId, this.cancelSubject);
      modalRef.componentInstance.maintenanceData = response.data;

      modalRef.closed.subscribe(() => this.dataRefreshNeeded.emit());
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          modalRef.dismiss();
        }
      });
    }
  }

  async deleteAssetMaintenance(request: DeleteMaintenanceRequest): Promise<void> {
    try {
      await this.maintenanceService.deleteAssetMaintenance(request);
      this.dataRefreshNeeded.emit();
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  onDeleteClick(itemId: number): void {
    const request: DeleteMaintenanceRequest = {
      maintenanceId: itemId,
      assetId: this.maintenanceData.find(m => m.id === itemId)?.assetId ?? 0
    };

    const options = createDeleteMaintenanceConfirmation(this.transloco, () => this.deleteAssetMaintenance(request));

    this.confirmationModalService.showConfirmationModal(options);
  }

  formatCurrency(value: number): string {
    if (!value) return '-';
    return `${this.currencySymbol}${value.toLocaleString('default', { maximumFractionDigits: 2 })}`;
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatRangeLeft(rangeLeft: number): string {
    if (!rangeLeft) return '-';
    return `${rangeLeft.toLocaleString('default', { maximumFractionDigits: 2 })} km`;
  }

  formatMaintenanceType(isAutoTrigger: boolean, type: ServiceTypes): string {
    return isAutoTrigger
      ? this.serviceTypeLabels[type]
      : this.transloco.translate('maintenanceOverview.reactiveMaintenanceType', { serviceType: this.serviceTypeLabels[type] });
  }

  getMaintenanceModel(model: MaintenanceModel): string {
    return model === MaintenanceModel.InHouse
      ? this.transloco.translate('maintenanceOverview.model.inHouse')
      : this.transloco.translate('maintenanceOverview.model.contract');
  }

  getMaintenanceSource(source: MaintenanceSource): string {
    switch (source) {
      case MaintenanceSource.OEM:
        return this.transloco.translate('maintenanceOverview.source.oem');
      case MaintenanceSource.Custom:
        return this.transloco.translate('maintenanceOverview.source.custom');
      case MaintenanceSource.System:
        return this.transloco.translate('maintenanceOverview.source.system');
      case MaintenanceSource.DriverApp:
        return this.transloco.translate('maintenanceOverview.source.driverApp');
      case MaintenanceSource.ComplianceApp:
        return this.transloco.translate('maintenanceOverview.source.complianceApp');
      case MaintenanceSource.CanBus:
        return this.transloco.translate('maintenanceOverview.source.canBus');
      default:
        return '';
    }
  }

  getStatusLabel(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.Approved:
        return this.transloco.translate('maintenanceOverview.status.approved');
      case MaintenanceStatus.Overdue:
        return this.transloco.translate('maintenanceOverview.status.overdue');
      case MaintenanceStatus.Complete:
        return this.transloco.translate('maintenanceOverview.status.complete');
      case MaintenanceStatus.Pending:
        return this.transloco.translate('maintenanceOverview.status.pending');
      default:
        return '';
    }
  }

  getStatusBadgeClass(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.Approved:
        return 'status-badge--approved';
      case MaintenanceStatus.Overdue:
        return 'status-badge--overdue';
      case MaintenanceStatus.Complete:
        return 'status-badge--complete';
      case MaintenanceStatus.Pending:
        return 'status-badge--pending';
      default:
        return '';
    }
  }
}